import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { environment } from '../../../environments/environment';

/**
 * Push notification client scaffold (native only).
 *
 * Requests permission, registers with APNs/FCM, and captures the device token.
 *
 * BACKEND DEPENDENCY: there is no endpoint yet to store the device token
 * against the account, nor a sender to emit payment-reminder / return-due /
 * new-message pushes. `registerDeviceToken` posts to a proposed
 * `POST /external/devices` endpoint and swallows failures until the backend
 * lands. Wire-up is in place; delivery is a separate backend task.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  constructor(private readonly http: HttpClient) {}

  /** Call once after login on native. Safe no-op on web. */
  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    PushNotifications.removeAllListeners();

    PushNotifications.addListener('registration', (token: Token) => {
      this.registerDeviceToken(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error', err);
    });

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === 'granted') {
      await PushNotifications.register();
    }
  }

  /**
   * Send the device token to the backend. Endpoint is proposed, not yet built;
   * failures are logged and ignored so the app keeps working without it.
   */
  private registerDeviceToken(token: string): void {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'
    // TODO(backend): implement POST /external/devices { token, platform }.
    this.http
      .post(`${environment.apiUrl}/external/devices`, { token, platform })
      .subscribe({
        next: () => {},
        error: (err) => console.warn('Device token registration not available yet', err?.status),
      });
  }
}
